import {
  IdmsTasksDocument,
  IdmsUploadsDocument,
  IdmsUploadsTypeEnum,
  useIdmsFileUploadMutation,
  useValidateIdmsFileLazyQuery,
} from '@/__generated__/graphql';
import { CsvParsedData, CsvValidationIssue } from '@/components/Analysis/Idms/types';
import { csvStringToJson } from '@/components/Analysis/Idms/utils';
import { convertToCsvString } from '@/utils';
import { useSnackBarMessage } from '@/components/ZonesManagementRevamped/utils';
import { useCallback, useState } from 'react';
import { useSetAtom } from 'jotai';
import * as analysisState from '@/components/Analysis/state';
import ExcelJS from 'exceljs';

export function useIdmsData(structureId: string, isPlannerColumn?: boolean | undefined) {
  const [selectedFileType, setSelectedFileType] = useState<IdmsUploadsTypeEnum | undefined>(
    undefined
  );
  const [uploadedCsvParsedData, setUploadedCsvParsedData] = useState<CsvParsedData>(undefined);
  const [idmsValidationErrors, setIdmsValidationErrors] = useState<CsvValidationIssue | undefined>(
    undefined
  );
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [fileExceptionsModal, setFileExceptionsModal] = useState(false);
  const [validateFile] = useValidateIdmsFileLazyQuery();
  const [idmsUploads, { loading: idmsUploadLoading }] = useIdmsFileUploadMutation();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const setIdmsUploadSequence = useSetAtom(analysisState.idmsUploadSequence);
  const validateFileContent = useCallback(
    (csvString: string, structureId: string | undefined, fileType?: IdmsUploadsTypeEnum) => {
      const templateType = fileType || selectedFileType;
      if (!templateType || !structureId) {
        return;
      }
      setIsValidating(true);
      validateFile({
        variables: {
          input: {
            csvString,
            template: templateType,
            structureId,
          },
        },
      })
        .then((data) => {
          if (data?.data?.validateIdmsTemplate) {
            setIdmsValidationErrors(undefined);
            setIsValidating(false);
            setOpenConfirmationDialog(true);
            return;
          }
          const issue: CsvValidationIssue | undefined = (() => {
            const exception = data?.error?.graphQLErrors?.[0]?.extensions?.exception;
            if (exception && typeof exception === 'object' && 'issue' in exception) {
              return exception.issue as CsvValidationIssue;
            }
            return undefined;
          })();
          setIdmsValidationErrors(issue);
          setFileExceptionsModal(true);
          setIsValidating(false);
        })
        .catch((error) => {
          const issue: CsvValidationIssue | undefined = (() => {
            const exception = error?.graphQLErrors?.[0]?.extensions?.exception;
            if (exception && typeof exception === 'object' && 'issue' in exception) {
              return exception.issue as CsvValidationIssue;
            }
            return undefined;
          })();
          setIdmsValidationErrors(issue);
          setFileExceptionsModal(true);
          setIsValidating(false);
        });
    },

    [
      selectedFileType,
      validateFile,
      setIdmsValidationErrors,
      setIsValidating,
      setOpenConfirmationDialog,
      setFileExceptionsModal,
    ]
  );

  const handleCsvFileUpload = (file: File, fileType?: IdmsUploadsTypeEnum) => {
    const reader = new FileReader();

    reader.addEventListener('load', (readEvent) => {
      const text = readEvent.target?.result?.toString();

      if (!text) return;

      const { header, rows } = csvStringToJson(text);
      const sanitizedRows = rows.map((row) => {
        const sanitizedRow: Record<string, unknown> = {};

        Object.entries(row).forEach(([key, value]) => {
          const stringValue = value?.toString() ?? '';
          sanitizedRow[key] = stringValue.replace(/^[^a-zA-Z0-9]+/, '');
        });

        return sanitizedRow;
      });
      setUploadedCsvParsedData({ header, rows: sanitizedRows });
      validateFileContent(text, structureId, fileType);
    });

    if (file) reader.readAsText(file);
  };

  const handleXlsxFileUpload = (file: File, fileType?: IdmsUploadsTypeEnum) => {
    const reader = new FileReader();

    reader.addEventListener('load', async (readEvent) => {
      try {
        if (!readEvent.target?.result) return;

        const arrayBuffer = readEvent.target.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);

        const worksheet = workbook.worksheets[0];
        if (!worksheet) {
          throw new Error('No worksheet found in the Excel file');
        }

        // Extract headers (first row)
        const header: string[] = [];
        worksheet.getRow(1).eachCell((cell) => {
          header.push(cell.value?.toString() || '');
        });

        // Extract data rows
        const rows: Record<string, unknown>[] = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return; // Skip header row

          const rowData: Record<string, unknown> = {};
          row.eachCell((cell, colNumber) => {
            if (colNumber <= header.length) {
              const raw = cell.value?.toString() || '';
              const sanitized = raw.replace(/^[^a-zA-Z0-9]+/, '');
              rowData[header[colNumber - 1]] = sanitized;
            }
          });

          rows.push(rowData);
        });

        // Convert Excel data to CSV format for validation
        const csvString = convertToCsvString(header, rows);

        setUploadedCsvParsedData({ header, rows });
        validateFileContent(csvString, structureId, fileType);
      } catch (error) {
        console.error('Error processing XLSX file:', error);
        setIdmsValidationErrors({
          type: 'validation',
          error: [
            {
              line: 0,
              errorMap: {
                XLSX_ERROR: [
                  {
                    message: 'Error processing XLSX file',
                    code: 'XLSX_ERROR',
                  },
                ],
              },
            },
          ],
        });
        setFileExceptionsModal(true);
        setIsValidating(false);
      }
    });

    reader.addEventListener('error', () => {
      setIdmsValidationErrors({
        type: 'validation',
        error: [
          {
            line: 0,
            errorMap: {
              FILE_READ_ERROR: [
                {
                  message: 'Error reading the file',
                  code: 'FILE_READ_ERROR',
                },
              ],
            },
          },
        ],
      });
      setFileExceptionsModal(true);
      setIsValidating(false);
    });

    if (file) reader.readAsArrayBuffer(file);
  };

  const handleIdmsFileUpload = (file?: File | undefined, validRecordsFile?: Blob) => {
    if (!file) return;

    const reader = new FileReader();

    reader.addEventListener('load', () => {
      const base64File = reader.result?.toString().split(',')[1];
      if (base64File && selectedFileType) {
        idmsUploads({
          variables: {
            input: {
              name: file.name,
              structureId,
              file: base64File,
              templateType: selectedFileType,
              isPlannerColumn,
            },
          },
          refetchQueries: [IdmsUploadsDocument, IdmsTasksDocument],
        })
          .then(() => {
            showSnackBar('Data updated successfully', 'success');
            setSelectedFileType(undefined);
            setFileExceptionsModal(false);
            setOpenConfirmationDialog(false);
            // Refetch the EquipmentTable
            setIdmsUploadSequence((s) => (s ?? 0) + 1);
          })
          .catch((error) => {
            console.error('IDMS upload failed', error);
            setIsValidating(false);
          });
      }
    });

    if (validRecordsFile) reader.readAsDataURL(validRecordsFile);
    else reader.readAsDataURL(file);
  };
  return {
    handleIdmsFileUpload,
    validateFileContent,
    setUploadedCsvParsedData,
    setSelectedFileType,
    setOpenConfirmationDialog,
    setFileExceptionsModal,
    setIdmsValidationErrors,
    setIsValidating,
    handleCsvFileUpload,
    handleXlsxFileUpload,
    idmsUploadLoading,
    selectedFileType,
    uploadedCsvParsedData,
    idmsValidationErrors,
    openConfirmationDialog,
    isValidating,
    fileExceptionsModal,
  };
}
